// Türkçe-uyumlu metin dönüştürme yardımcıları — form giriş normalizasyonu için.

/**
 * Title Case ("ahmet çınar" → "Ahmet Çınar", "İNŞAAT AŞ" → "İnşaat Aş").
 * Türkçe locale ile "İ"/"ı" ayrımı korunur.
 */
export const titleCaseTR = (s) => {
  if (!s) return s;
  return String(s)
    .toLocaleLowerCase("tr-TR")
    .split(/(\s+)/) // ayırıcı boşlukları koru
    .map((part) => {
      if (!part || !part.trim()) return part;
      return part.charAt(0).toLocaleUpperCase("tr-TR") + part.slice(1);
    })
    .join("");
};

/** Türkçe-uyumlu küçük harf. */
export const toLowerTR = (s) => String(s || "").toLocaleLowerCase("tr-TR");

/** Türkçe-uyumlu büyük harf. */
export const toUpperTR = (s) => String(s || "").toLocaleUpperCase("tr-TR");

/** Sadece rakamları tutar, max N haneye keser. */
export const digitsOnly = (s, max = 11) => String(s || "").replace(/\D+/g, "").slice(0, max);

/** Plaka: alfanümerik + boşluk, uppercase (Türkçe locale). */
export const plateUpperTR = (s) =>
  String(s || "")
    .replace(/[^a-zA-Z0-9çÇğĞıİöÖşŞüÜ ]+/g, "")
    .toLocaleUpperCase("tr-TR");

/** İrsaliye no / kod: alfanümerik + tire/altçizgi, uppercase. */
export const codeUpperTR = (s) =>
  String(s || "")
    .replace(/[^a-zA-Z0-9\-_./ ]+/g, "")
    .toLocaleUpperCase("tr-TR");

/**
 * Arama için normalize eder: Türkçe locale ile küçük harf + diakritikleri kaldırır.
 * "İSTANBUL" → "istanbul", "Çınar" → "cinar", "şantiye" → "santiye".
 * Böylece kullanıcı "istanbul" da yazsa "İstanbul" kaydını bulur.
 */
/**
 * Akıllı Türkçe Title Case — kullanıcı ALL CAPS veya all lowercase
 * yazdığında "Ahmet Çınar" gibi çevirir; karma yazımı korur.
 * Not defterlerinde ve açıklamalarda son kullanıcı deneyimini iyileştirmek için.
 *
 *  "TEMMUZ AYI YEMEK"   → "Temmuz Ayı Yemek"
 *  "temmuz ayı yemek"    → "Temmuz Ayı Yemek"
 *  "Temmuz Ayı Yemek"    → "Temmuz Ayı Yemek"   (değişmez)
 *  "iSG-2024/ceza"       → "iSG-2024/ceza"      (karma, değişmez)
 *  ""                    → ""                    (boş, değişmez)
 */
export const smartTitleCaseTR = (s) => {
  const raw = String(s || "");
  const trimmed = raw.trim();
  if (!trimmed) return raw;
  // Harf içermiyorsa dokunma (sadece rakam/tire vb.)
  const letters = trimmed.replace(/[^\p{L}]/gu, "");
  if (!letters) return raw;
  const upper = letters.toLocaleUpperCase("tr-TR");
  const lower = letters.toLocaleLowerCase("tr-TR");
  const isAllUpper = letters === upper;
  const isAllLower = letters === lower;
  if (!(isAllUpper || isAllLower)) return raw;
  // Satır sonlarını ve boşlukları koru; her satırı ayrı ayrı işle
  return raw.split(/(\r?\n)/).map((line) => {
    if (/^\r?\n$/.test(line) || !line) return line;
    return line.split(/(\s+)/).map((word) => {
      if (!word || /^\s+$/.test(word)) return word;
      const lo = word.toLocaleLowerCase("tr-TR");
      return lo.charAt(0).toLocaleUpperCase("tr-TR") + lo.slice(1);
    }).join("");
  }).join("");
};


// Kasa yazım kuralı: kelime başı büyük, gerisi küçük; yalnız kısaltmalar büyük kalır.
// Backend (routes/kasa.py) ile aynı varsayılan liste; bilinmeyen BÜYÜK kelimeler
// yazarken korunur, alandan çıkınca sunucu (AI destekli) son kararı verir.
export const KASA_DEFAULT_ABBR = new Set([
  "KDV", "ÖTV", "SGK", "TDK", "ATO", "İTO", "TEİAŞ", "TEDAŞ", "TCDD", "TOKİ",
  "İSKİ", "İGDAŞ", "BOTAŞ", "PTT", "THY", "AŞ", "LTD", "ŞTİ", "TL", "USD",
  "EUR", "İSG", "KKD", "EFT", "POS", "ATM", "IBAN", "PDF", "TC", "SSK",
]);
const KASA_CAPS_ONLY = new Set(["AŞ"]);

export const kasaCaseTR = (s) => {
  if (!s) return s;
  return String(s).split(/([\s/\-_.]+)/).map((tok) => {
    if (!tok || /^[\s/\-_.]+$/.test(tok)) return tok;
    const letters = tok.replace(/[^\p{L}]/gu, "");
    const up = letters.toLocaleUpperCase("tr-TR");
    const isCaps = letters.length >= 2 && letters === up;
    if (KASA_DEFAULT_ABBR.has(up) && (!KASA_CAPS_ONLY.has(up) || isCaps)) {
      return tok.toLocaleUpperCase("tr-TR");
    }
    if (isCaps) return tok;
    const i = tok.search(/[\p{L}\p{N}]/u);
    if (i < 0) return tok;
    return tok.slice(0, i) + tok.charAt(i).toLocaleUpperCase("tr-TR")
      + tok.slice(i + 1).toLocaleLowerCase("tr-TR");
  }).join("");
};

// onChange için: formatla ve imleç konumunu koru.
export const kasaCaseInput = (e, apply) => {
  const el = e.target;
  const pos = el.selectionStart;
  apply(kasaCaseTR(el.value));
  requestAnimationFrame(() => { try { el.setSelectionRange(pos, pos); } catch (_) { /* noop */ } });
};


const DIACRITIC_MAP = {
  "ç": "c", "ğ": "g", "ı": "i", "İ": "i", "ö": "o", "ş": "s", "ü": "u",
  "Ç": "c", "Ğ": "g", "I": "i", "Ö": "o", "Ş": "s", "Ü": "u",
};
export const normalizeTR = (s) => {
  if (!s) return "";
  return String(s)
    .toLocaleLowerCase("tr-TR")
    .replace(/[çğıİöşüÇĞIÖŞÜ]/g, (ch) => DIACRITIC_MAP[ch] || ch)
    .trim();
};

/**
 * Türkçe-uyumlu, diakritik-duyarsız substring eşleşme.
 * `needle` boşluk ayrılmış birden fazla parça içeriyorsa, hepsi haystack'te bulunmalı.
 */
export const searchMatchTR = (needle, haystack) => {
  const n = normalizeTR(needle);
  if (!n) return true;
  const h = normalizeTR(haystack);
  return n.split(/\s+/).filter(Boolean).every((tok) => h.includes(tok));
};
